import { useCallback, useEffect, useReducer, useRef } from "react";
import axios from "axios";
import type { StudentTestAnswer, StudentTestContent, UserInfo } from "p6m-p6u";
import { SchoolClassVisibilities } from "../../enums/schoolClasses";
import { Views } from "../../enums/views";
import { NetworkErrorCodes } from "../../networking/errorCodes";
import type { ClassMember } from "../../networking/schoolClasses";
import { getClassByToken } from "../../networking/schoolClasses";
import { StudentTestAccessStates } from "../../helpers/OnlineTestHelper";
import { StudentTestLoadOutcomes } from "../../context/StudentPracticeContext";
import useTestPersistence from "../../hooks/useTestPersistence";
import {
    LoadFailureKinds,
    OnlineTestActionTypes,
    OnlineTestPhases,
    getClassFacts,
    getIsClassMemberSessionReady,
    getIsPrivateAccess,
    getIsPrivateClass,
    getPersistedAnswersDecision,
    getPersistedAnswersOwnerId,
    getSchoolClassId,
    hasResolvedIdentity,
    initialOnlineTestState,
    onlineTestReducer,
    type IdentityFacts,
    type OnlineTestState,
} from "./onlineTestMachine";

interface UseOnlineTestMachineParams {
    /** Absent on `/create/preview`, where there is no share record to load. */
    testId: string | undefined;
    /** From StudentPracticeContext. `{}` until the fetch lands. */
    teacherTest: StudentTestContent;
    /** True for a record from either source — see the two step-1 effects. */
    hasTestRecord: boolean;
    isPreview: boolean;
    isOwner: boolean;
    member: ClassMember | null;
    isRestoringSession: boolean;
    userInfo: UserInfo | undefined;
    userId: string;
    isUserTestsLoaded: boolean;
    /**
     * `isUserTestsLoaded` goes true even when the fetch failed, leaving the list empty but not authoritative.
     * A 403 sets neither flag — it redirects to /unauthorized instead, and that unmount is the only thing
     * that stops RESOLVING_IDENTITY waiting on a lookup that has already given up.
     */
    hasUserTestsLoadError: boolean;
    isTestOwner: boolean;
    /** Unmemoized at its source, so it must not appear in a dependency array. */
    loadUserTests: () => void;
    /** Fetches the shared test into the context and reports whether it is gone or merely unreachable. */
    refreshData: (testId: string) => Promise<StudentTestLoadOutcomes>;

    /* Passed straight through to `useTestPersistence`, which this hook owns. */
    studentTestAnswer: StudentTestAnswer;
    currentMainView: Views | number;
    currentExerciseId: string;
    countdownStartedAt: number;
    setStudentTestAnswer: (val: StudentTestAnswer) => void;
    setCurrentMainView: (val: Views | number) => void;
    setCurrentExerciseId: (val: string) => void;
    setCountdownStarted: (val: boolean) => void;
    setCountdownStartedAt: (val: number) => void;
    setTestEndTimestamp: (val: number) => void;
}

interface UseOnlineTestMachineResult {
    state: OnlineTestState;
    /** null until RESOLVING_IDENTITY has answered. */
    identity: IdentityFacts | null;
    /** Uppercased, so callers can compare and route without normalising again. undefined before the load. */
    schoolClassId: string | undefined;
    isLegacyActivity: boolean;
    isPrivateClass: boolean;
    /** A member of this test's own private class. */
    isPrivateAccess: boolean;
    /**
     * That same member, or null when this is not private access — the identity a submission is filed under.
     * Returned rather than re-derived by the caller so it cannot be paired with a different member.
     */
    privateAccessMember: ClassMember | null;
    /**
     * Reports the access answer back in. Not a param: the hooks that decide it take this machine's own
     * class and identity facts as inputs, so passing it in would be a forward reference.
     */
    reportAccess: (access: StudentTestAccessStates, needsPrivateLogin: boolean) => void;

    /* From `useTestPersistence`, which this hook owns so that its inputs need no forward reference. */
    rehydrated: boolean;
    restoredSubmittedState: boolean;
    clearPersistedAnswers: () => void;
}

/**
 * Drives `onlineTestReducer`: the reducer decides what state follows, these effects decide when to act
 * and do the I/O. One effect per step of the sequence.
 *
 * Each opens with a phase guard rather than trusting its dependency array — several of the values they
 * read are unmemoized, so an effect may re-run on any render and the guard makes that a no-op.
 */
const useOnlineTestMachine = (params: UseOnlineTestMachineParams): UseOnlineTestMachineResult => {
    const [state, dispatch] = useReducer(onlineTestReducer, initialOnlineTestState);

    // Nothing resets the phases: `/student/:testId` is only ever entered from a sibling route, so the
    // component always mounts fresh. Cross-test navigation from inside the test view would need a reset.
    const isSharedRecord = !!params.testId;
    const isAuthResolved = !!params.userInfo?.visitorOrUserRole || !!params.userInfo?.isAnonymousUser;
    const isTeacher = params.userInfo?.visitorOrUserRole?.toLowerCase() === "teacher";

    const isMemberSessionSettled = !params.isRestoringSession;

    /*
     * A ref, not reducer state: dispatching is asynchronous, so under StrictMode's mount/unmount/remount the
     * second run still reads pre-dispatch state and fires a duplicate request. Measured, not theoretical.
     * (The class fetch needs no equivalent — it keys on an id that only exists inside LOADING_CLASS, so its
     * re-run is idempotent.)
     */
    const hasRequestedLoadRef = useRef(false);

    /*
     * Owner detection wipes stored answers, but `useTestPersistence` is called further down — it consumes
     * the identity this very effect produces. A ref closes that loop without leaving the hook.
     */
    const clearPersistedAnswersRef = useRef<() => void>(() => {});

    /*
     * Step 1 of 4: does this test exist? Split across the next two effects, because a record reaches the
     * context two ways: `/student/:testId` fetches a shared one, `/create/preview` already holds the
     * author's draft and requests nothing.
     *
     * This one asks, and only it sees the outcome — so only it can classify a failure. It cannot announce
     * success: it never runs on the preview route, and `refreshData` returns an outcome rather than the
     * record.
     */
    useEffect(() => {
        if (state.phase !== OnlineTestPhases.LOADING_TEST) return;
        if (params.isPreview || !params.testId || hasRequestedLoadRef.current) return;

        hasRequestedLoadRef.current = true;

        // No `cancelled` guard: the ref makes this one-shot, so cancelling on unmount would discard the
        // only response that will ever arrive and leave a blank screen instead of the error card.
        (async () => {
            const outcome = await params.refreshData(params.testId!);
            if (outcome === StudentTestLoadOutcomes.LOADED) return; // nothing to report: only failures land here

            dispatch({
                type: OnlineTestActionTypes.TEST_FAILED,
                kind:
                    outcome === StudentTestLoadOutcomes.NOT_FOUND
                        ? LoadFailureKinds.TERMINAL
                        : LoadFailureKinds.RECOVERABLE,
            });
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.phase, params.isPreview, params.testId]);

    // ...and this one announces that a record has arrived, without caring which route put it there.
    useEffect(() => {
        if (state.phase !== OnlineTestPhases.LOADING_TEST || !params.hasTestRecord) return;
        dispatch({
            type: OnlineTestActionTypes.TEST_LOADED,
            test: params.teacherTest,
            isSharedRecord,
        });
    }, [state.phase, params.hasTestRecord, params.teacherTest, isSharedRecord]);

    // Step 2 of 4: private class or public? `cancelled` covers unmount only — nothing else can leave
    // LOADING_CLASS, since both ways out are dispatched right here.
    const schoolClassId = state.phase === OnlineTestPhases.LOADING_CLASS ? getSchoolClassId(state.test) : undefined;

    useEffect(() => {
        if (!schoolClassId) return;

        let cancelled = false;
        (async () => {
            try {
                const schoolClass = await getClassByToken(schoolClassId);
                if (cancelled) return;
                /*
                 * Keyed on `token`, not on truthiness or on the visibility: `{}` is truthy, and a body whose
                 * visibility is missing reads as "not private", which would drop the login gate on a private
                 * class. Every real record carries a token, so its absence means the fetch failed.
                 */
                if (!schoolClass?.token) throw new Error("Class metadata carried no token");
                dispatch({
                    type: OnlineTestActionTypes.CLASS_LOADED,
                    visibility: schoolClass.classVisibility ?? SchoolClassVisibilities.PUBLIC,
                });
            } catch (e) {
                if (cancelled) return;
                console.error("Failed to fetch class metadata", e);
                // A class that is gone stays gone; anything else (5xx, offline, timeout) is worth
                // another attempt. Guarded `response?.status` — a network error has no response at all.
                const isNotFound = axios.isAxiosError(e) && e.response?.status === NetworkErrorCodes.NOT_FOUND;
                dispatch({
                    type: OnlineTestActionTypes.CLASS_FAILED,
                    kind: isNotFound ? LoadFailureKinds.TERMINAL : LoadFailureKinds.RECOVERABLE,
                });
            }
        })();

        return () => {
            cancelled = true;
        };
    }, [schoolClassId]);

    const isResolvingIdentity = state.phase === OnlineTestPhases.RESOLVING_IDENTITY;
    const hasRequestedOwnerLookup = isResolvingIdentity && state.hasRequestedOwnerLookup;

    // Step 3 of 4: who is this? A teacher who owns the test gets preview, everyone else the student flow.
    useEffect(() => {
        if (!isResolvingIdentity || !isMemberSessionSettled) return;

        /*
         * The create-flow preview owns its draft by construction, so it answers before the auth check
         * below — waiting there would stall on a round-trip that never comes.
         */
        if (params.isPreview || !params.testId) {
            dispatch({
                type: OnlineTestActionTypes.IDENTITY_RESOLVED,
                identity: { isOwner: params.isOwner, isPreview: true, member: params.member },
            });
            return;
        }

        if (!isAuthResolved) return;

        if (!isTeacher) {
            dispatch({
                type: OnlineTestActionTypes.IDENTITY_RESOLVED,
                identity: { isOwner: false, isPreview: false, member: params.member },
            });
            return;
        }

        // `loadUserTests` no-ops while `userId` is falsy, so this guard has to stay ahead of the lookup
        // latch: latching first would leave the phase waiting on a request that was never made.
        if (!params.userId) return;

        if (!params.isUserTestsLoaded) {
            if (hasRequestedOwnerLookup) return;
            dispatch({ type: OnlineTestActionTypes.OWNER_LOOKUP_REQUESTED });
            params.loadUserTests();
            return;
        }

        /*
         * An empty list is not authoritative when the request failed — every test would look like someone
         * else's, including the author's own. Teacher-only branch, so no student is affected.
         */
        if (params.hasUserTestsLoadError) {
            // The other two load failures log at their own catch; this one has no throw of its own to report.
            console.error("Failed to load the teacher's test list, so ownership could not be established");
            dispatch({ type: OnlineTestActionTypes.OWNER_LOOKUP_FAILED });
            return;
        }

        // Only wipe stored answers when the test list was already loaded, so no request of ours was
        // needed. On the async path they may belong to a student rather than to this teacher's preview.
        if (params.isTestOwner && !hasRequestedOwnerLookup) {
            clearPersistedAnswersRef.current();
        }

        dispatch({
            type: OnlineTestActionTypes.IDENTITY_RESOLVED,
            identity: { isOwner: params.isTestOwner, isPreview: params.isTestOwner, member: params.member },
        });
        // `loadUserTests` is absent on purpose: it is re-created every render at its source, so listing it
        // would re-run this every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        isResolvingIdentity,
        hasRequestedOwnerLookup,
        isMemberSessionSettled,
        isAuthResolved,
        isTeacher,
        params.isPreview,
        params.testId,
        params.userId,
        params.isUserTestsLoaded,
        params.hasUserTestsLoadError,
        params.isTestOwner,
        params.member,
        params.isOwner,
    ]);

    // Re-asks step 3 when the person changes mid-mount: ClassMemberContext.login() is a plain state
    // update, and so is the cross-tab storage listener.
    const memberId = params.member?.id ?? null;
    const previousMemberIdRef = useRef(memberId);
    useEffect(() => {
        if (previousMemberIdRef.current === memberId) return;
        previousMemberIdRef.current = memberId;
        dispatch({
            type: OnlineTestActionTypes.IDENTITY_CHANGED,
            identity: { isOwner: params.isOwner, isPreview: params.isPreview, member: params.member },
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [memberId]);

    // Step 4 of 4: may they take it? Answered by the component, since the hooks that decide it consume
    // this machine's own class and identity facts.
    const reportAccess = useCallback((access: StudentTestAccessStates, needsPrivateLogin: boolean) => {
        dispatch({ type: OnlineTestActionTypes.ACCESS_RESOLVED, access, needsPrivateLogin });
    }, []);

    // Keyed on `state`, not on the values read out of it: this identity change is what re-triggers
    // rehydration (it is one of its deps), and without it a held record would stay held forever.
    const decidePersistedAnswers = useCallback(
        (storedOwner: string | null) => getPersistedAnswersDecision(state, storedOwner),
        [state]
    );

    const classFacts = getClassFacts(state);
    const testFacts = "test" in state ? state.test : null;
    /*
     * The machine's member, not `params.member`: IDENTITY_CHANGED arrives by effect, so for one render after
     * a login the live member is already here while the machine still holds the old one. Everything below is
     * derived from this one value, so nothing downstream can pair an access answer with a different member.
     */
    const identityMember = hasResolvedIdentity(state) ? state.identity.member : null;
    const isPrivateAccess = testFacts && classFacts ? getIsPrivateAccess(testFacts, classFacts, identityMember) : false;

    /*
     * Persistence lives here because its inputs are the machine's own answers — whose record this is, and
     * whether identity has settled. From the component those would be forward references.
     */
    const persistence = useTestPersistence({
        teacherTest: params.teacherTest,
        studentTestAnswer: params.studentTestAnswer,
        currentMainView: params.currentMainView,
        currentExerciseId: params.currentExerciseId,
        countdownStartedAt: params.countdownStartedAt,
        // Never the context copy: on `/student/:id` it is still false here, so an owner would rehydrate
        // as a student. What that costs them is on the skip itself, in `useTestPersistence`.
        isPreview: hasResolvedIdentity(state) ? state.identity.isPreview : params.isPreview,
        // Same identity `getPersistedAnswersDecision` reads: disagree and it answers HOLD for a member who is
        // already present, and nothing changes afterwards to release the record.
        persistedAnswersOwnerId:
            testFacts && classFacts ? getPersistedAnswersOwnerId(testFacts, classFacts, identityMember) : null,
        getPersistedAnswersDecision: decidePersistedAnswers,
        isClassMemberSessionReady: getIsClassMemberSessionReady(state),
        setStudentTestAnswer: params.setStudentTestAnswer,
        setCurrentMainView: params.setCurrentMainView,
        setCurrentExerciseId: params.setCurrentExerciseId,
        setCountdownStarted: params.setCountdownStarted,
        setCountdownStartedAt: params.setCountdownStartedAt,
        setTestEndTimestamp: params.setTestEndTimestamp,
    });

    clearPersistedAnswersRef.current = persistence.clearPersistedAnswers;

    return {
        state,
        identity: hasResolvedIdentity(state) ? state.identity : null,
        schoolClassId: testFacts ? getSchoolClassId(testFacts) : undefined,
        isLegacyActivity: testFacts?.isLegacy ?? false,
        /*
         * All false/null until the phase that establishes them is reached, which is the point: a `false`
         * here means "not yet or no", and the phase says which.
         */
        isPrivateClass: classFacts ? getIsPrivateClass(classFacts) : false,
        isPrivateAccess,
        privateAccessMember: isPrivateAccess ? identityMember : null,
        reportAccess,
        rehydrated: persistence.rehydrated,
        restoredSubmittedState: persistence.restoredSubmittedState,
        clearPersistedAnswers: persistence.clearPersistedAnswers,
    };
};

export default useOnlineTestMachine;
